import React, { useCallback, useEffect, useState } from 'react'
import { adminApi } from '../services/api'
import { Loader, Modal } from '../components/UI'
import { formatToman, toFaDigits } from '../utils/format'
import toast from 'react-hot-toast'

interface ProductRow {
  id: string
  name: string
  slug: string
  type: 'GOLD' | 'SILVER'
  category: 'SHMESH' | 'ABSHADE' | 'COIN' | 'BAR'
  purity: number
  basePrice: number
  markupPercent: number
  buybackPercent: number | null
  sellingPrice: number
  buybackPrice: number
  isActive: boolean
  description?: string
}

const EMPTY_FORM = {
  id: '',
  name: '',
  slug: '',
  description: '',
  type: 'GOLD' as 'GOLD' | 'SILVER',
  category: 'SHMESH' as 'SHMESH' | 'ABSHADE' | 'COIN' | 'BAR',
  weight: 100,
  purity: 750,
  markupPercent: 5,
  buybackPercent: -2,
  sortOrder: 0
}

/**
 * درصد منفی خرید مؤثر: اگر برای محصول مقدار اختصاصی ثبت نشده باشد،
 * از قیمت‌های محاسبه‌شده (که با تنظیم سراسری ساخته شده‌اند) استخراج می‌شود.
 */
const effectiveBuybackPercent = (p: ProductRow): number => {
  if (p.buybackPercent !== null && p.buybackPercent !== undefined) return p.buybackPercent
  if (!p.basePrice) return 0
  return Math.round((p.buybackPrice / p.basePrice - 1) * 1000) / 10
}

const Products: React.FC = () => {
  const [products, setProducts] = useState<ProductRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState<any>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await adminApi.getProducts({ search: search || undefined, type: typeFilter || undefined, limit: 100 })
      if (res.data.success && res.data.data) setProducts(res.data.data.products ?? [])
    } catch {
      toast.error('خطا در دریافت محصولات')
    } finally {
      setLoading(false)
    }
  }, [search, typeFilter])

  useEffect(() => {
    load()
  }, [load])

  const saveMarkup = async (id: string, value: number) => {
    try {
      const res = await adminApi.updateMarkup(id, value)
      if (res.data.success && res.data.data) {
        setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, ...res.data.data! } : p)))
        toast.success('درصد سود محصول ذخیره شد')
      }
    } catch {
      toast.error('خطا در ذخیره درصد سود')
    }
  }

  const saveBuyback = async (id: string, value: number) => {
    try {
      const res = await adminApi.updateBuyback(id, value)
      if (res.data.success && res.data.data) {
        setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, ...res.data.data! } : p)))
        toast.success('درصد منفی خرید ذخیره شد')
      }
    } catch {
      toast.error('خطا در ذخیره درصد منفی خرید')
    }
  }

  const openCreate = () => {
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (p: ProductRow) => {
    setForm({ ...EMPTY_FORM, ...p, description: p.description || '' })
    setModalOpen(true)
  }

  const submitForm = async () => {
    setSaving(true)
    try {
      if (form.id) {
        const res = await adminApi.updateProduct(form.id, form)
        if (res.data.success) toast.success('محصول به‌روزرسانی شد')
      } else {
        const res = await adminApi.createProduct(form)
        if (res.data.success) toast.success('محصول ساخته شد')
      }
      setModalOpen(false)
      load()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خطا در ذخیره محصول')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (p: ProductRow) => {
    try {
      const res = await adminApi.updateProduct(p.id, { isActive: !p.isActive })
      if (res.data.success && res.data.data) {
        setProducts((prev) => prev.map((x) => (x.id === p.id ? res.data.data! : x)))
        toast.success(p.isActive ? 'محصول غیرفعال شد' : 'محصول فعال شد')
      }
    } catch {
      toast.error('خطا در تغییر وضعیت')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">محصولات</h1>
          <p className="text-sm text-gray-500 mt-1">{toFaDigits(products.length)} محصول</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ ساخت محصول جدید</button>
      </div>

      <div className="flex flex-wrap gap-3">
        <input className="input !w-72" placeholder="جستجوی نام محصول…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input !w-auto" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">همه فلزات</option>
          <option value="GOLD">طلا</option>
          <option value="SILVER">نقره</option>
        </select>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader /></div>
      ) : (
        <div className="card overflow-hidden">
          <div className="table-wrap">
            <table className="table-admin">
              <thead>
                <tr>
                  <th>محصول</th>
                  <th>نوع</th>
                  <th>قیمت خام</th>
                  <th>درصد سود</th>
                  <th>درصد منفی خرید</th>
                  <th>قیمت فروش</th>
                  <th>قیمت خرید از مشتری</th>
                  <th>وضعیت</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className={p.isActive ? '' : 'opacity-50 bg-gray-50'}>
                    <td>
                      <div className="font-bold text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-400" dir="ltr">{p.slug}</div>
                    </td>
                    <td>
                      <span className={`badge ${p.type === 'GOLD' ? 'bg-gold-100 text-gold-800' : 'bg-gray-200 text-gray-700'}`}>
                        {p.type === 'GOLD' ? 'طلا' : 'نقره'} / عیار {toFaDigits(p.purity)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">{formatToman(p.basePrice)}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          dir="ltr"
                          className="w-14 border border-gray-300 rounded px-1 py-0.5 text-center font-bold"
                          value={p.markupPercent}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value)
                            setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, markupPercent: isNaN(v) ? 0 : v } : x)))
                          }}
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value)
                            if (!isNaN(v) && v !== p.markupPercent) saveMarkup(p.id, v)
                          }}
                        />
                        <span className="text-xs">٪</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={-100}
                          max={100}
                          dir="ltr"
                          className="w-16 border border-gray-300 rounded px-1 py-0.5 text-center font-bold text-red-600"
                          value={effectiveBuybackPercent(p)}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value)
                            setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, buybackPercent: isNaN(v) ? 0 : v } : x)))
                          }}
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value)
                            if (!isNaN(v) && v !== effectiveBuybackPercent(p)) saveBuyback(p.id, v)
                          }}
                        />
                        <span className="text-xs">٪</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap font-bold text-primary-700">{formatToman(p.sellingPrice)}</td>
                    <td className="whitespace-nowrap">{formatToman(p.buybackPrice)}</td>
                    <td>
                      <span className={`badge ${p.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {p.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button className="btn btn-secondary !py-1 !px-2 !text-xs" onClick={() => openEdit(p)}>ویرایش</button>
                        <button
                          className={`btn !py-1 !px-2 !text-xs ${p.isActive ? '!bg-red-500 !text-white' : '!bg-green-500 !text-white'}`}
                          onClick={() => toggleActive(p)}
                        >
                          {p.isActive ? 'غیرفعال' : 'فعال'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
<Modal open={modalOpen} title={form.id ? 'ویرایش محصول' : 'ساخت محصول جدید'} onClose={() => setModalOpen(false)} wide>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label">نام محصول *</label>
            <input className="input" value={form.name} onChange={(e) => setForm((p: any) => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">نامک (slug) *</label>
            <input className="input" dir="ltr" value={form.slug} onChange={(e) => setForm((p: any) => ({ ...p, slug: e.target.value }))} />
          </div>
          <div>
            <label className="label">نوع فلز *</label>
            <select className="input" value={form.type} onChange={(e) => setForm((p: any) => ({ ...p, type: e.target.value }))}>
              <option value="GOLD">طلا</option>
              <option value="SILVER">نقره</option>
            </select>
          </div>
          <div>
            <label className="label">دسته *</label>
            <select className="input" value={form.category} onChange={(e) => setForm((p: any) => ({ ...p, category: e.target.value }))}>
              <option value="SHMESH">شمش</option>
              <option value="ABSHADE">آب‌شده</option>
              <option value="COIN">سکه</option>
              <option value="BAR">بار</option>
            </select>
          </div>
          <div>
            <label className="label">وزن (گرم) *</label>
            <input type="number" className="input" dir="ltr" value={form.weight} onChange={(e) => setForm((p: any) => ({ ...p, weight: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <label className="label">عیار (مثلاً ۷۵۰ یا ۹۹۹٫۹) *</label>
            <input type="number" className="input" dir="ltr" value={form.purity} onChange={(e) => setForm((p: any) => ({ ...p, purity: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <label className="label">درصد سود *</label>
            <input type="number" min={0} max={100} className="input" dir="ltr" value={form.markupPercent} onChange={(e) => setForm((p: any) => ({ ...p, markupPercent: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <label className="label">درصد منفی خرید (کسر خرید از مشتری)</label>
            <input type="number" min={-100} max={100} className="input" dir="ltr" value={form.buybackPercent} onChange={(e) => setForm((p: any) => ({ ...p, buybackPercent: parseFloat(e.target.value) || 0 }))} />
            <p className="text-[11px] text-gray-400 mt-1">
              مثلاً ‎-۲ یعنی ما همان محصول را ۲٪ کمتر از قیمت خام از مشتری می‌خریم.
            </p>
          </div>
          <div>
            <label className="label">ترتیب نمایش</label>
            <input type="number" className="input" dir="ltr" value={form.sortOrder} onChange={(e) => setForm((p: any) => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="sm:col-span-2">
            <label className="label">توضیحات</label>
            <textarea className="input min-h-20" value={form.description} onChange={(e) => setForm((p: any) => ({ ...p, description: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>انصراف</button>
          <button className="btn btn-primary" disabled={saving} onClick={submitForm}>
            {saving ? 'در حال ذخیره…' : form.id ? 'ذخیره تغییرات' : 'ساخت محصول'}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default Products
